<template lang="pug">
v-container(fluid)
  v-card
    v-card-title.d-flex.align-center.justify-space-between
      span Caja Fuerte · Documentación
      v-spacer
      v-btn(text small class="mr-2" @click="seedCats" :loading="seeding" )
        v-icon(left) mdi-database-plus
        | Categorías base
      v-btn(text small class="mr-2" @click="goCategories")
        v-icon(left) mdi-folder-cog
        | Categorías
      v-btn(color="primary" @click="openCreateDialog")
        v-icon(left) mdi-plus
        | Nuevo documento
  v-card.mt-3
    v-card-text
      v-alert(type="info" v-if="!hasCategories" outlined dense)
        | No tenés categorías creadas. Usá "Categorías base" para generarlas rápidamente (Córdoba, AR).
      v-row
        v-col(cols="12" sm="4")
          v-text-field(v-model="filters.q" label="Buscar" prepend-icon="mdi-magnify" clearable @input="debouncedFetch")
        v-col(cols="12" sm="4")
          v-select(:items="categoryItems" item-text="name" item-value="_id" v-model="filters.category" label="Categoría" clearable @change="fetchDocuments")
        v-col(cols="12" sm="4")
          v-select(:items="statusItems" v-model="filters.status" label="Estado" clearable @change="fetchDocuments")
      v-data-table(:headers="headers" :items="documents" :loading="loading" dense)
        template(v-slot:item.title="{ item }")
          a(@click.prevent="openDetail(item)" href="#") {{ item.title }}
        template(v-slot:item.category="{ item }")
          span {{ findCategoryName(item.category) || '-' }}
        template(v-slot:item.updatedAt="{ item }")
          span {{ formatDate(item.updatedAt) }}
        template(v-slot:item.actions="{ item }")
          v-btn(icon small @click="openDetail(item)")
            v-icon mdi-eye
          v-btn(icon small @click="openUploadDialog(item)")
            v-icon mdi-tray-arrow-up
          v-btn(icon small @click="downloadCurrent(item)" :disabled="!item.currentVersion")
            v-icon mdi-cloud-download
  // Dialog crear documento
  v-dialog(v-model="createDialog" max-width="600")
    v-card
      v-card-title Nuevo documento
      v-card-text
        v-form(ref="createForm" v-model="createValid")
          v-text-field(v-model="createData.title" label="Título" :rules="[v=>!!v||'Requerido']" required)
          v-select(:items="categoryItems" item-text="name" item-value="_id" v-model="createData.category" label="Categoría" clearable)
          v-combobox(v-model="createData.tags" :items="tagItems" label="Etiquetas" multiple chips small-chips clearable)
          v-textarea(v-model="createData.description" label="Descripción" rows="2")
      v-card-actions
        v-spacer
        v-btn(text @click="createDialog=false") Cancelar
        v-btn(color="primary" :disabled="!createValid" @click="createDocument") Crear
  // Dialog subir versión
  v-dialog(v-model="uploadDialog" max-width="500")
    v-card
      v-card-title Subir archivo (máx 50MB)
      v-card-text
        v-alert(type="info" outlined dense)
          | Sólo PDF o imágenes (JPG, PNG, WEBP). Córdoba - AR
        v-form(ref="uploadForm" v-model="uploadValid")
          v-file-input(v-model="file" label="Archivo" accept="application/pdf,image/*" :rules="fileRules" show-size)
          v-text-field(v-model="notes" label="Notas (opcional)")
      v-card-actions
        v-spacer
        v-btn(text @click="uploadDialog=false") Cancelar
        v-btn(color="primary" :loading="uploading" :disabled="!uploadValid || !file" @click="performUpload") Subir
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'VaultDocuments',
  data: () => ({
    filters: { q: '', category: null, status: null },
    headers: [
      { text: 'Título', value: 'title' },
      { text: 'Categoría', value: 'category' },
      { text: 'Estado', value: 'status' },
      { text: 'Actualizado', value: 'updatedAt' },
      { text: 'Acciones', value: 'actions', sortable: false }
    ],
    createDialog: false,
    createValid: false,
    createData: { title: '', category: null, tags: [], description: '' },
    uploadDialog: false,
    uploadValid: false,
    targetDoc: null,
    file: null,
    notes: '',
    uploading: false,
    seeding: false,
    statusItems: [
      { text: 'Vigente', value: 'vigente' },
      { text: 'Próximo a vencer', value: 'proximo' },
      { text: 'Vencido', value: 'vencido' },
      { text: 'Archivado', value: 'archivado' }
    ],
    fileRules: [
      v => !!v || 'Seleccioná un archivo',
      v => !v || v.size <= 50 * 1024 * 1024 || 'Máximo 50MB'
    ],
    debouncedId: null
  }),
  computed: {
    ...mapState('vaultDocuments', ['categories', 'list', 'loading']),
    documents() { return this.list || [] },
    categoryItems() { return this.categories || [] },
    tagItems() { return this.$store.state.vaultDocuments.tags || [] },
    hasCategories() { return (this.categories || []).length > 0 }
  },
  methods: {
    ...mapActions('vaultDocuments', ['fetchCategories', 'fetch', 'create', 'requestPresign', 'attachVersion', 'getDownloadUrl', 'seedCategories', 'fetchTags']),
    formatDate(d) { try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: es }) } catch { return d } },
    findCategoryName(id) { const c = this.categories.find(x => x._id === id); return c ? c.name : null },
    async fetchDocuments() { await this.fetch(this.filters) },
    debouncedFetch() { clearTimeout(this.debouncedId); this.debouncedId = setTimeout(this.fetchDocuments, 300) },
    async seedCats() {
      try {
        this.seeding = true
        await this.seedCategories()
        await this.fetchCategories()
        this.$toast.success('Categorías base creadas')
      } catch (e) { this.$toast.error('No se pudieron crear las categorías') }
      finally { this.seeding = false }
    },
    openCreateDialog() { this.createDialog = true },
    goCategories() { this.$router.push({ name: 'ManagementVaultCategories' }) },
    openDetail(item) { this.$router.push({ name: 'ManagementVaultDetail', params: { id: item._id } }) },
    async createDocument() {
      if (!this.$refs.createForm.validate()) return
      const doc = await this.create(this.createData)
      this.createDialog = false
      this.$toast.success('Documento creado')
      this.openUploadDialog(doc)
    },
    openUploadDialog(doc) { this.targetDoc = doc; this.file = null; this.notes = ''; this.uploadDialog = true },
    async performUpload() {
      if (!this.$refs.uploadForm.validate()) return
      try {
        this.uploading = true
        // 1) Presign PUT
        const presign = await this.requestPresign({ filename: this.file.name, contentType: this.file.type, size: this.file.size })
        // 2) PUT directo a S3
        await fetch(presign.url, { method: 'PUT', headers: { 'Content-Type': this.file.type }, body: this.file })
        // 3) Confirmar versión
        await this.attachVersion({ documentId: this.targetDoc._id, key: presign.key, filename: this.file.name, mime: this.file.type, size: this.file.size, checksum: null, notes: this.notes })
        this.$toast.success('Archivo subido')
        this.uploadDialog = false
        await this.fetchDocuments()
      } catch (e) {
        this.$toast.error('Error al subir archivo')
      } finally { this.uploading = false }
    },
    async downloadCurrent(doc) {
      try {
        const { url } = await this.getDownloadUrl({ documentId: doc._id })
        window.open(url, '_blank')
      } catch (e) { this.$toast.error('No se pudo descargar') }
    }
  },
  async created() {
    await this.fetchCategories()
    await this.fetchTags()
    await this.fetchDocuments()
  }
}
</script>
